import { Col, Row } from "antd";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import PaymentAmount from "./components/PaymentAmount";
import { useCallback, useEffect, useState } from "react";
import { ResponseCisPoints, ResponsePaymentGeneral, Tarjeta } from "../../types/common";
import { getCisPoint, getExpirationCard, getPaymentsGeneral } from "../../methods/Methods";
import PaymentCistList from "./components/PaymentCisList";
import InfoCardComponent from "../../components/InfoCardComponent";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../stores/slices/generalDataSlice";
import Loading from "../../components/Loading";
import { getPaymentDataContracts, setPaymentDataToContract } from "../../stores/slices/paymentDataSlice";
import { AppDispatch } from "../../stores/store";
import { setLoading } from "../../stores/slices/loadingSlice";

export interface CisData {
    name: string,
    latitude: string,
    longitude: string,
    address: string,
    suburb: string,
    id: string,
    distance?: string
}

const PaymentCis = () => {
    const geolib = require('geolib');
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const paymentDataRedux = useSelector(getPaymentDataContracts);
    const [paymentData, setPaymentData] = useState<ResponsePaymentGeneral>();
    const [cisPointsArray, setCisPointsArray] = useState<Array<CisData>>([]);
    const [hasError, setHasError] = useState(false);
    const [message, setMessage] = useState("");
    const generalData = useSelector(getGeneralData);
    const [idCliente, setIdCliente] = useState<string>("");
    const [packageName, setPackageName] = useState<string>("");

    const getPaymentData = useCallback(async () => {
        dispatch(setLoading({ isLoading: true }));
        await getPaymentsGeneral(idCliente)
            .then(async (response) => {
                if (!_.isEmpty(response.data)) {
                    let recurrentCard: Tarjeta | null = null;
                    await getExpirationCard(idCliente)
                        .then((response) => {
                            if (response.data && response.data.data[0]) {
                                //Si el campo tarjeta no está null es porque tiene tarjeta domiciliada
                                //Y si es diferente del cod 902
                                if (!_.isNull(response.data.data[0].tarjeta) && response.data.data[0].codigo !== "902") {
                                    recurrentCard = response.data.data[0].tarjeta;
                                }
                            }
                        })
                        .catch(() => {
                        });

                    setPaymentData(response.data[0]);
                    dispatch(setPaymentDataToContract({
                        clientId: idCliente,
                        paymentData: response.data[0],
                        recurrentCard: recurrentCard
                    }));
                }
                else {
                    setPaymentData(undefined);
                    dispatch(setLoading({ isLoading: false }));
                }
            }).catch(() => {
                setPaymentData(undefined);
                dispatch(setLoading({ isLoading: false }));
            });
    }, [dispatch, idCliente]);

    useEffect(() => {
        if (generalData.selectedContract !== null) {
            setIdCliente(
                generalData.selectedContract.sucursal + generalData.selectedContract.suscriptor
            );
            setPackageName(generalData.selectedContract.paquete)
        }
    }, [generalData.selectedContract]);

    useEffect(() => {
        if (!_.isEmpty(idCliente)) {
            setIsLoading(true);
            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(async (position) => {
                    let currLatitude = position.coords.latitude.toString();
                    let currLongitude = position.coords.longitude.toString();
                    await getCisPoint(idCliente, currLatitude, currLongitude)
                        .then((response) => {
                            if (response && response.data && response.data.data[0]) {
                                if (!_.isEmpty(response.data.data[0])) {
                                    let cisPointsArrayTmp = response.data.data[0];
                                    if (cisPointsArrayTmp) {
                                        let cisTmp: CisData[] = [];
                                        _.forEach(cisPointsArrayTmp, (cis: ResponseCisPoints) => {
                                            cisTmp.push({
                                                name: cis.nombre,
                                                latitude: cis.latitud,
                                                longitude: cis.longitud,
                                                address: _.truncate(cis.direccion, {
                                                    'length': 35,
                                                }),
                                                suburb: cis.colonia ? cis.colonia : "",
                                                id: cis.id_cis
                                            })
                                        })

                                        _.forEach(cisTmp, (cis) => {
                                            let distance = geolib.getDistance({ latitude: currLatitude, longitude: currLongitude }, cis, 1);
                                            if (distance >= 1000) {
                                                cis.distance = _.round(geolib.convertDistance(distance, 'km')).toString() + " KM"
                                            } else {
                                                cis.distance = distance.toString() + " M"
                                            }
                                        })
                                        let cisOrdened = geolib.orderByDistance({ latitude: currLatitude, longitude: currLongitude }, cisTmp);

                                        setCisPointsArray(cisOrdened);
                                    }
                                } else {
                                    setMessage("No hay CIS en su zona.");
                                }
                            }
                            setIsLoading(false);
                        })
                        .catch(() => {
                            setHasError(true);
                            setMessage("Hubo un error al procesar su solicitud. \nIntente más tarde.");
                            setIsLoading(false);
                        })
                }, (error) => {
                    setHasError(true);
                    setIsLoading(false);
                    setMessage("No se aceptaron los permisos de ubicación.");
                })
            }
        }
    }, [idCliente, geolib]);

    useEffect(() => {
        if (idCliente) {
            const index = paymentDataRedux.contracts.findIndex(obj => obj.clientId === idCliente);

            if (index >= 0) {
                setPaymentData(paymentDataRedux.contracts[index].paymentData);
            }
            else {
                getPaymentData();
            }
        }
    }, [idCliente, getPaymentData, paymentDataRedux.contracts]);

    return (
        <>
            {
                isLoading ?
                    <Loading />
                    :
                    <SubDefaultLayout title={"Pagar en CIS"} needReturn={true} navigateTo={"/payments"}>
                        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                            <Col xs={24}>
                                <PaymentAmount paymentData={paymentData} packageName={packageName} />
                            </Col>
                            {
                                hasError ?
                                    <Col xs={24} style={{ marginBottom: 20 }}>
                                        <InfoCardComponent title="¡Estimado usuario!" description={message} />
                                    </Col>
                                    :
                                    <Col xs={24} style={{ marginBottom: 20 }}>
                                        <PaymentCistList cisList={cisPointsArray} />
                                    </Col>
                            }
                        </Row>
                    </SubDefaultLayout>
            }
        </>
    )
}

export default PaymentCis;
